import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../contexts/ToastContext';
import { useApi } from '../hooks/useApi';
import { useKnowledgeBaseErrorHandling } from '../hooks/useErrorHandling';
import { formatFileSize } from '../utils/fileUtils.js';
import { getThemeClasses, getInputClasses, getButtonClasses, getCardClasses } from '../utils/themeUtils';
import LoadingSpinner from './LoadingSpinner';
import LoadingIndicator, { OperationStatus } from './LoadingIndicator';
import ErrorBoundary from './ErrorBoundary';
import EnhancedKnowledgeBaseSyncStatus, { BulkSyncStatus } from './EnhancedKnowledgeBaseSyncStatus';
import FileDeleteConfirmationDialog from './FileDeleteConfirmationDialog';

const ALLOWED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'text/plain': '.txt',
  'text/html': '.html'
};

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB in bytes

const KnowledgeBaseFileManager = ({ 
  selectedFiles = [], 
  onSelectionChange = null, 
  showSelection = false,
  showUsage = true,
  onFileChange = null,
  showUpload = true
}) => {
  const { getToken } = useAuth();
  const { showSuccess, showError } = useToast();
  const { execute } = useApi();
  const errorHandling = useKnowledgeBaseErrorHandling();
  const fileInputRef = useRef(null);
  
  // State management
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedFileIds, setSelectedFileIds] = useState(new Set(selectedFiles.map(f => f.id || f)));
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingFiles, setDeletingFiles] = useState(new Set());
  const [operationStatus, setOperationStatus] = useState(new Map()); // Track operation statuses
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    file: null,
    parrotUsage: []
  });

  // Load knowledge base files with enhanced error handling
  const fetchKnowledgeFiles = useCallback(async () => {
    setLoading(true);
    setOperationStatus(prev => new Map(prev.set('fetch_files', { status: 'running', message: 'Loading files...' })));
    
    try {
      const result = await errorHandling.executeWithErrorHandling(
        'fetch_knowledge_files',
        async () => {
          return await execute(async (api) => {
            const response = await api.get('/api/knowledge-base');
            const files = response.data || response.documents || response || [];
            // Ensure files is always an array
            const filesArray = Array.isArray(files) ? files : [];
            setKnowledgeFiles(filesArray);
            // Note: Removed onFileChange call here to prevent infinite loop
            // The parent component should manage its own state independently
            return filesArray;
          }, { showErrorToast: false }); // Let error handling service manage toasts
        },
        {
          context: { operation: 'fetch_files' },
          showSuccessMessage: false, // Don't show success for routine fetches
          retryable: true
        }
      );
      
      setOperationStatus(prev => new Map(prev.set('fetch_files', { status: 'success', message: 'Files loaded' })));
      
      // Clear status after a delay
      setTimeout(() => {
        setOperationStatus(prev => {
          const newMap = new Map(prev);
          newMap.delete('fetch_files');
          return newMap;
        });
      }, 2000);
      
    } catch (error) {
      setOperationStatus(prev => new Map(prev.set('fetch_files', { 
        status: 'error', 
        message: 'Failed to load files',
        onRetry: fetchKnowledgeFiles
      })));
    } finally {
      setLoading(false);
    }
  }, [execute, errorHandling]); // Removed onFileChange from dependencies to prevent infinite loop

  useEffect(() => {
    fetchKnowledgeFiles();
  }, [fetchKnowledgeFiles]);

  // Enhanced file upload handling with comprehensive error handling
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    
    // Pre-validate files
    const validFiles = [];
    for (const file of files) {
      // Validate file type
      if (!Object.keys(ALLOWED_FILE_TYPES).includes(file.type)) {
        errorHandling.showUserFriendlyError('INVALID_FILE_TYPE', { 
          fileName: file.name, 
          fileType: file.type 
        });
        continue;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        errorHandling.showUserFriendlyError('FILE_TOO_LARGE', { 
          fileName: file.name 
        });
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      setUploading(false);
      return;
    }

    try {
      // Use enhanced file upload handling
      const results = await errorHandling.handleKBFileUpload(validFiles);
      
      // Refresh file list after upload
      await fetchKnowledgeFiles();
      
      return results;
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setUploading(false);
    }
  }, [errorHandling, fetchKnowledgeFiles]);

  // Show delete confirmation dialog
  const showDeleteConfirmation = useCallback((file) => {
    const parrotUsage = file.parrotUsage && file.parrotUsage.parrotNames 
      ? file.parrotUsage.parrotNames 
      : [];
    
    setConfirmDialog({
      isOpen: true,
      file: file,
      parrotUsage: parrotUsage
    });
  }, []);

  // Cancel delete confirmation
  const cancelDelete = useCallback(() => {
    setConfirmDialog({
      isOpen: false,
      file: null,
      parrotUsage: []
    });
  }, []);

  // Enhanced file deletion with comprehensive error handling
  const confirmDelete = useCallback(async () => {
    const { file } = confirmDialog;
    if (!file) return;

    setDeletingFiles(prev => new Set([...prev, file.id]));
    setOperationStatus(prev => new Map(prev.set(`delete_${file.id}`, { 
      status: 'running', 
      message: `Deleting "${file.name}"...` 
    })));
    
    try {
      // Use enhanced delete handling
      const result = await errorHandling.handleKBFileDelete(file);
      
      if (result && !result.error) {
        // Close dialog first
        setConfirmDialog({
          isOpen: false,
          file: null,
          parrotUsage: []
        });

        // Update file list immediately (optimistic update)
        setKnowledgeFiles(prev => prev.filter(f => f.id !== file.id));
        
        // Update operation status
        setOperationStatus(prev => new Map(prev.set(`delete_${file.id}`, { 
          status: 'success', 
          message: `"${file.name}" deleted successfully` 
        })));
        
        // Refresh from server to ensure consistency
        await fetchKnowledgeFiles();
      } else {
        // Handle error case
        setOperationStatus(prev => new Map(prev.set(`delete_${file.id}`, { 
          status: 'error', 
          message: `Failed to delete "${file.name}"`,
          onRetry: () => confirmDelete()
        })));
        
        // Close dialog on error
        setConfirmDialog({
          isOpen: false,
          file: null,
          parrotUsage: []
        });
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
      
      setOperationStatus(prev => new Map(prev.set(`delete_${file.id}`, { 
        status: 'error', 
        message: `Failed to delete "${file.name}"`,
        onRetry: () => confirmDelete()
      })));
      
      // Close dialog on error
      setConfirmDialog({
        isOpen: false,
        file: null,
        parrotUsage: []
      });
    } finally {
      setDeletingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.id);
        return newSet;
      });
      
      // Clear operation status after delay
      setTimeout(() => {
        setOperationStatus(prev => {
          const newMap = new Map(prev);
          newMap.delete(`delete_${file.id}`);
          return newMap;
        });
      }, 3000);
    }
  }, [confirmDialog, errorHandling, fetchKnowledgeFiles]);

  // File selection handling
  const handleFileSelection = useCallback((fileId, selected) => {
    const newSelectedIds = new Set(selectedFileIds);
    if (selected) {
      newSelectedIds.add(fileId);
    } else {
      newSelectedIds.delete(fileId);
    }
    setSelectedFileIds(newSelectedIds);
    
    if (onSelectionChange) {
      const selectedFileObjects = (Array.isArray(knowledgeFiles) ? knowledgeFiles : []).filter(f => newSelectedIds.has(f.id));
      onSelectionChange(selectedFileObjects);
    }
  }, [selectedFileIds, onSelectionChange]); // Removed knowledgeFiles from dependencies to prevent infinite loop

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  }, [handleFileUpload]);

  // Filter files based on search and type
  const filteredFiles = (Array.isArray(knowledgeFiles) ? knowledgeFiles : []).filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || file.type === filterType;
    return matchesSearch && matchesType;
  });

  // Handle sync retry for individual files
  const handleSyncRetry = useCallback(async (fileId) => {
    await fetchKnowledgeFiles(); // Refresh the file list after retry
  }, [fetchKnowledgeFiles]);

  // Enhanced bulk retry with comprehensive error handling
  const handleBulkRetry = useCallback(async () => {
    const failedFiles = (Array.isArray(knowledgeFiles) ? knowledgeFiles : []).filter(f =>
      f.sync_status === 'failed' || f.sync_status === 'error'
    );
    
    if (failedFiles.length === 0) return;
    
    setOperationStatus(prev => new Map(prev.set('bulk_retry', {
      status: 'running',
      message: `Retrying ${failedFiles.length} failed files...`
    })));
    
    try {
      const result = await errorHandling.executeWithErrorHandling(
        'bulk_retry_sync',
        async () => {
          return await execute(async (api) => {
            const promises = failedFiles.map(file =>
              api.post(`/api/knowledge-base/${file.id}/retry-sync`)
            );
            const results = await Promise.all(promises);
            
            // Invalidate cache after retry sync operations
            api.invalidateCache('/api/knowledge-base');
            
            return results;
          }, { showErrorToast: false });
        },
        {
          context: {
            operation: 'bulk_retry',
            fileCount: failedFiles.length
          },
          successMessage: `Retrying ${failedFiles.length} failed files`,
          retryable: true
        }
      );
      
      if (result && !result.error) {
        setOperationStatus(prev => new Map(prev.set('bulk_retry', {
          status: 'success',
          message: `Retry initiated for ${failedFiles.length} files`
        })));
        
        await fetchKnowledgeFiles();
      } else {
        setOperationStatus(prev => new Map(prev.set('bulk_retry', {
          status: 'error',
          message: 'Failed to retry some files',
          onRetry: handleBulkRetry
        })));
      }
    } catch (error) {
      console.error('Failed to retry bulk sync:', error);
      setOperationStatus(prev => new Map(prev.set('bulk_retry', {
        status: 'error',
        message: 'Failed to retry some files',
        onRetry: handleBulkRetry
      })));
    } finally {
      // Clear operation status after delay
      setTimeout(() => {
        setOperationStatus(prev => {
          const newMap = new Map(prev);
          newMap.delete('bulk_retry');
          return newMap;
        });
      }, 5000);
    }
  }, [knowledgeFiles, errorHandling, execute, fetchKnowledgeFiles]); // Note: Keeping knowledgeFiles in dependencies as it's needed for filtering failed files

  const theme = getThemeClasses();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <ErrorBoundary 
      fallbackMessage="Unable to load Knowledge Base files. Please refresh the page."
      context={{ component: 'KnowledgeBaseFileManager' }}
    >
      <div className="space-y-6">
        {/* Operation Status Display */}
        {operationStatus.size > 0 && (
          <div className="space-y-2">
            {Array.from(operationStatus.entries()).map(([key, status]) => (
              <OperationStatus
                key={key}
                operation={status.message}
                status={status.status}
                onRetry={status.onRetry}
              />
            ))}
          </div>
        )}

        {/* Bulk sync status */}
        {knowledgeFiles.length > 0 && (
          <BulkSyncStatus 
            files={knowledgeFiles}
            onRetryAll={handleBulkRetry}
          />
        )}
        {/* Header with upload and search */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <input
              type="text"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={getInputClasses()}
            />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className={getInputClasses()}
            >
              <option value="all">All Types</option>
              <option value="file">Files</option>
              <option value="text">Text</option>
            </select>
          </div>
          
          {showUpload && (
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={Object.values(ALLOWED_FILE_TYPES).join(',')}
                onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className={getButtonClasses('primary', 'medium', uploading)}
              >
                {uploading ? (
                  <div className="flex items-center space-x-2">
                    <LoadingIndicator type="spinner" size="small" color="white" />
                    <span>Uploading...</span>
                  </div>
                ) : (
                  'Upload Files'
                )}
              </button>
            </div>
          )}
        </div>

        {/* Drag and drop area */}
        {showUpload && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' 
                : `${theme.border.secondary} hover:border-gray-400 dark:hover:border-gray-500`
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="space-y-2">
              <div className={theme.text.tertiary}>
                <svg className={`mx-auto h-12 w-12 ${theme.text.muted}`} stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className={`text-sm ${theme.text.secondary}`}>
                <span className={`font-medium cursor-pointer ${theme.text.link}`} onClick={() => fileInputRef.current?.click()}>
                  Click to upload
                </span> or drag and drop
              </div>
              <p className={`text-xs ${theme.text.muted}`}>
                PDF, DOCX, TXT, HTML files up to 4MB
              </p>
            </div>
          </div>
        )}

        {/* Files list */}
        {filteredFiles.length === 0 ? (
          <div className="text-center py-12">
            <div className={theme.text.muted}>
              {(Array.isArray(knowledgeFiles) ? knowledgeFiles : []).length === 0 ? 'No knowledge base files yet' : 'No files match your search criteria'}
            </div>
          </div>
        ) : (
          <div className={getCardClasses()}>
            <ul className={`divide-y ${theme.border.primary}`}>
              {filteredFiles.map((file) => (
                <li key={file.id} className="px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex items-start flex-1 min-w-0">
                      {showSelection && (
                        <input
                          type="checkbox"
                          checked={selectedFileIds.has(file.id)}
                          onChange={(e) => handleFileSelection(file.id, e.target.checked)}
                          className="mr-3 mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded flex-shrink-0 dark:border-gray-600"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${theme.text.primary}`}>
                              {file.name}
                            </p>
                            <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm ${theme.text.secondary}`}>
                              <span className="flex-shrink-0">{file.type}</span>
                              {file.size && (
                                <>
                                  <span className="hidden sm:inline">•</span>
                                  <span className="flex-shrink-0">{file.formattedSize || formatFileSize(file.size)}</span>
                                </>
                              )}
                              <span className="hidden sm:inline">•</span>
                              <span className="flex-shrink-0">{new Date(file.created_at).toLocaleDateString()}</span>
                            </div>
                            {/* Parrot usage information */}
                            {showUsage && file.parrotUsage && file.parrotUsage.count > 0 && (
                              <div className="mt-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                                  <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                  </svg>
                                  <span className="truncate">
                                    Used by {file.parrotUsage.count} parrot{file.parrotUsage.count !== 1 ? 's' : ''}: {file.parrotUsage.parrotNames.join(', ')}
                                  </span>
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <button
                              onClick={() => showDeleteConfirmation(file)}
                              disabled={deletingFiles.has(file.id)}
                              className={`text-sm px-2 py-1 rounded transition-colors ${
                                deletingFiles.has(file.id) 
                                  ? `${theme.text.muted} cursor-not-allowed` 
                                  : `${theme.text.error} hover:bg-red-50 dark:hover:bg-red-900/20`
                              }`}
                            >
                              {deletingFiles.has(file.id) ? (
                                <div className="flex items-center">
                                  <LoadingIndicator type="spinner" size="small" color="secondary" />
                                  <span className="ml-2">Deleting...</span>
                                </div>
                              ) : (
                                'Delete'
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {/* Enhanced sync status */}
                        <div className="mt-3">
                          <EnhancedKnowledgeBaseSyncStatus
                            file={file}
                            onRetry={handleSyncRetry}
                            compact={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <FileDeleteConfirmationDialog
          isOpen={confirmDialog.isOpen}
          file={confirmDialog.file}
          parrotUsage={confirmDialog.parrotUsage}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          loading={confirmDialog.file ? deletingFiles.has(confirmDialog.file.id) : false}
        />
      </div>
    </ErrorBoundary>
  );
};

export default KnowledgeBaseFileManager;
